// Approval Chains Logic (PO-5)
// Checks if orders/actions require approval based on configured rules

import type { ApprovalPolicy, ApprovalRule, ApprovalRequest } from './types'

export interface ApprovalCheck {
  required: boolean
  reason?: string
  rule?: ApprovalRule
  approverRole?: 'ADMIN' | 'OWNER' | 'ADMIN_OR_OWNER'
}

/**
 * Check if an action requires approval based on policy and amount
 */
export function checkApprovalRequired(
  policy: ApprovalPolicy | null | undefined,
  params: {
    amount: number
    entityType: 'purchase_order' | 'rfq' | 'supplier_change'
    requesterRole: string
  }
): ApprovalCheck {
  if (!policy || !policy.enabled) {
    return { required: false }
  }

  // Owner never needs approval
  if (params.requesterRole === 'OWNER') {
    return { required: false }
  }

  // Check specific rules first (ordered by minAmount desc for most specific match)
  const applicableRules = (policy.rules || [])
    .filter((r) => r.isActive && r.appliesTo.includes(params.entityType))
    .sort((a, b) => (b.minAmount ?? 0) - (a.minAmount ?? 0))

  for (const rule of applicableRules) {
    const minAmount = rule.minAmount ?? 0
    const maxAmount = rule.maxAmount ?? Infinity

    if (params.amount >= minAmount && params.amount <= maxAmount) {
      return {
        required: true,
        reason: `Amount ₦${params.amount.toLocaleString()} requires ${rule.requiresApprovalFrom.toLowerCase()} approval`,
        rule,
        approverRole: rule.requiresApprovalFrom,
      }
    }
  }

  // Fall back to default threshold
  if (params.amount >= policy.defaultThreshold) {
    return {
      required: true,
      reason: `Amount exceeds default threshold of ₦${policy.defaultThreshold.toLocaleString()}`,
      approverRole: 'ADMIN_OR_OWNER',
    }
  }

  return { required: false }
}

/**
 * Get the minimum role required to approve an amount
 */
export function getRequiredApproverRole(
  policy: ApprovalPolicy | null | undefined,
  amount: number,
  entityType: 'purchase_order' | 'rfq' | 'supplier_change' = 'purchase_order'
): 'ADMIN' | 'OWNER' | 'ADMIN_OR_OWNER' | null {
  const check = checkApprovalRequired(policy, {
    amount,
    entityType,
    requesterRole: 'STAFF', // Assume lowest role for checking
  })

  return check.approverRole ?? null
}

/**
 * Check if a user can approve a request based on their role
 */
export function canApprove(
  userRole: string,
  requiredRole: 'ADMIN' | 'OWNER' | 'ADMIN_OR_OWNER'
): boolean {
  if (userRole === 'OWNER') return true
  if (requiredRole === 'OWNER') return userRole === 'OWNER'
  if (requiredRole === 'ADMIN_OR_OWNER') return userRole === 'ADMIN' || userRole === 'OWNER'
  if (requiredRole === 'ADMIN') return userRole === 'ADMIN' || userRole === 'OWNER'
  return false
}

/**
 * Format approval status for display
 */
export function formatApprovalStatus(status: string): string {
  const labels: Record<string, string> = {
    pending: 'Pending Approval',
    approved: 'Approved',
    rejected: 'Rejected',
    escalated: 'Escalated',
  }
  return labels[status] || status
}

/**
 * Default approval policy for new branches
 */
export const DEFAULT_APPROVAL_POLICY: ApprovalPolicy = {
  enabled: false,
  defaultThreshold: 100000, // ₦100,000
  rules: [
    {
      id: 'default_high_value',
      branchId: '', // Set at runtime
      name: 'High Value Orders',
      minAmount: 500000, // ₦500,000
      maxAmount: null,
      requiresApprovalFrom: 'OWNER',
      appliesTo: ['purchase_order', 'rfq'],
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: 'default_medium_value',
      branchId: '', // Set at runtime
      name: 'Medium Value Orders',
      minAmount: 100000, // ₦100,000
      maxAmount: 499999,
      requiresApprovalFrom: 'ADMIN_OR_OWNER',
      appliesTo: ['purchase_order', 'rfq'],
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  ],
  notifyVia: ['push', 'in_app'],
  escalationHours: 24,
}

/**
 * Create an approval request object
 */
export function createApprovalRequest(
  params: {
    branchId: string
    requesterId: string
    requesterRole: string
    entityType: 'purchase_order' | 'rfq' | 'supplier_change'
    entityId: string
    amount: number | null
    ruleId?: string | null
    reason?: string
  }
): Omit<ApprovalRequest, 'id'> {
  const expiresAt = new Date()
  expiresAt.setHours(expiresAt.getHours() + 48) // 48 hour expiry

  return {
    branchId: params.branchId,
    requesterId: params.requesterId,
    requesterRole: params.requesterRole,
    approverId: null,
    ruleId: params.ruleId ?? null,
    entityType: params.entityType,
    entityId: params.entityId,
    amount: params.amount,
    status: 'pending',
    reason: params.reason ?? null,
    requestedAt: new Date().toISOString(),
    respondedAt: null,
    expiresAt: expiresAt.toISOString(),
  }
}
